import { VerificationCountsByLocationIdsRequest } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { formatDate } from '@frontend/date';
import { i18next } from '@frontend/i18n';
import { getFullName } from '@frontend/name';

export const getPersonIdsFromAppointments = (appointments: Appointment[] | CalendarEvent[], locationId: string) => {
  return appointments.reduce((acc, appointment) => {
    if ('person' in appointment) {
      if (appointment?.person?.personId && appointment?.locationId === locationId) {
        acc.push(appointment?.person?.personId);
      }
    }

    if ('attendeeId' in appointment) {
      if (appointment?.attendeeId && appointment?.locationId === locationId) {
        acc.push(appointment?.attendeeId);
      }
    }

    return acc;
  }, [] as string[]);
};

export const getVerificationCountsRequestByLocationIds = (
  appointments: Appointment[] | CalendarEvent[],
  locationIds: string[]
): VerificationCountsByLocationIdsRequest => ({
  locationPersonIds: locationIds.map((locationId) => ({
    locationId,
    personIds: getPersonIdsFromAppointments(appointments, locationId),
  })),
});
//Below functions are picked from schedule-pulse codebase in order to maintain consistency in displaying appointment time across the app
export const getEventDurationFromStartDate = (startDate: string, duration?: number) => {
  const startHour = formatDate(dayjs(startDate).toDate(), 'hh:mm A');
  const endHour = duration ? formatDate(dayjs(startDate).add(duration, 'm').toDate(), 'hh:mm A') : '';
  return getDurationFromHours(startHour, endHour);
};

export const getDurationFromHours = (startHour: string, endHour: string) => {
  let durationString = '';
  const startTimeMatch = startHour === '00:00' || startHour === '12:00 AM';
  const endTimeMatch = endHour === '23:59' || endHour === '11:59 PM';

  if (startTimeMatch && endTimeMatch) {
    durationString = 'All day';
  } else {
    durationString = `${timestampTo12HourFormat(startHour, true)} ${
      endHour ? ` -  ${timestampTo12HourFormat(endHour, true)}` : ''
    }`;
  }
  return durationString;
};
const timestampTo12HourFormat = (timestamp: string | number, showMeridiem = false): string => {
  const time = parseTimestamp(timestamp);

  return time.format(showMeridiem ? 'h:mm A' : 'h:mm');
};
const parseTimestamp = (timestamp: string | number) => {
  let time = dayjs(timestamp);

  if (!time.isValid()) {
    time = dayjs(timestamp, 'hh:mm A');
  }

  if (!time.isValid()) {
    time = dayjs(timestamp, 'HH:mm');
  }

  if (!time.isValid()) {
    time = dayjs(timestamp, 'H A');
  }

  if (!time.isValid()) {
    throw new Error('Invalid timestamp format');
  }

  return time;
};

const constructFullName = (person: Appointment['person'] | CalendarEvent['attendeeDetails']) => {
  return getFullName(person || {}) || i18next.t('Unknown', { ns: 'insurance-details' });
};

const getAppointmentDurationAndStartDate = (appointment: Appointment | CalendarEvent, isScheduleV3Enabled: boolean) => {
  if (isScheduleV3Enabled) {
    const calendarEvent = appointment as CalendarEvent;
    return {
      appointmentDuration: calendarEvent?.details?.dur || 0,
      appointmentStartDateTime: calendarEvent?.startDate + '' + calendarEvent?.startTime,
    };
  }

  const appointmentDetails = appointment as Appointment;
  return {
    appointmentStartDateTime: appointmentDetails?.start || '',
    appointmentDuration: appointmentDetails?.duration || 0,
  };
};

export const prepareAppointmentData = (
  appointment: Appointment | CalendarEvent,
  isScheduleV3Enabled: boolean,
  insuranceStatus: string
) => {
  const name = constructFullName(
    isScheduleV3Enabled ? (appointment as CalendarEvent).attendeeDetails : (appointment as Appointment).person
  );

  const { appointmentStartDateTime, appointmentDuration } = getAppointmentDurationAndStartDate(
    appointment,
    isScheduleV3Enabled
  );

  const duration = getEventDurationFromStartDate(appointmentStartDateTime, appointmentDuration);
  return {
    id: appointment.id || '',
    name,
    appointmentTime: duration,
    insuranceStatus,
    locationId: appointment.locationId || '',
  };
};
